import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { SurfaceCard } from "@/components/common/Cards";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Loader2, Check, ClipboardList, ChevronLeft, ChevronRight, Mic } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ImagingUploadPanel } from "@/components/common/ImagingUploadPanel";

export default function DynamicFormFill() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const condition = params.get("condition") || "implant";
  const tier = params.get("tier") || "quick";
  const navigate = useNavigate();
  const [form, setForm] = useState(undefined);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/forms/effective?condition=${condition}`).then((r) => {
      const f = r.data;
      setForm(f && tier === "quick" ? { ...f, questions: (f.questions || []).filter((q) => q.tier !== "comprehensive") } : f);
    }).catch(() => setForm(null));
    api.get(`/consultations/${id}`).then((r) => { if (r.data?.form_answers) setAnswers(r.data.form_answers); }).catch(() => {});
  }, [condition, tier, id]);

  const sections = useMemo(() => {
    if (!form) return [];
    const map = {};
    (form.questions || []).forEach((q) => { const s = q.section || "General"; (map[s] = map[s] || []).push(q); });
    return Object.entries(map).map(([title, qs]) => ({ title, qs }));
  }, [form]);

  const steps = useMemo(() => [...sections.map((s) => ({ ...s, kind: "section" })), { kind: "imaging", title: "Investigations & Imaging" }, { kind: "review", title: "Review & Analyze" }], [sections]);
  const [imgState, setImgState] = useState({ count: 0, pending: 0, analyzing: 0 });
  const total = (form?.questions || []).length;
  const done = Object.values(answers).filter((v) => v !== "" && v != null).length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  const setAns = (qid, v) => setAnswers((a) => ({ ...a, [qid]: v }));

  const save = async (extra = {}) => {
    const cc = (form?.questions || []).find((q) => /chief complaint/i.test(q.label));
    await api.put(`/consultations/${id}`, {
      form_id: form?.id, condition, form_answers: answers,
      chief_complaint: cc ? answers[cc.id] || "" : undefined, ...extra,
    });
  };
  const saveDraft = async () => { await save(); toast.success("Draft saved"); };

  const analyze = async () => {
    setBusy(true);
    try {
      await save({ status: "in_progress" });
      const { data } = await api.post("/ai/treatment-plan", { consultation_id: id });
      toast.success("Analyzing case — doctor review required.");
      navigate(`/treatment-plan/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); setBusy(false); }
  };

  if (form === undefined) return <Skeleton className="h-[60vh] rounded-2xl" />;
  if (!form) return (
    <SurfaceCard className="p-8 text-center max-w-md mx-auto mt-10">
      <ClipboardList className="mx-auto h-8 w-8 text-[#8B9698]" />
      <h2 className="mt-3 text-lg font-bold text-[#101516] dark:text-white">No form configured</h2>
      <p className="mt-1 text-sm text-[#8B9698]">This condition has no active clinical form yet.</p>
      <Button onClick={() => navigate("/forms")} className="mt-4 bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg">Manage Forms</Button>
    </SurfaceCard>
  );

  const field = (q) => {
    const v = answers[q.id] ?? "";
    const manualOnly = q.voice_capturable === false;
    const inp = (() => {
      if (q.type === "long_text") return <Textarea data-testid={`field-${q.id}`} value={v} onChange={(e) => setAns(q.id, e.target.value)} rows={2} className="mt-1.5" />;
      if (q.type === "number" || q.type === "measurement") return <Input data-testid={`field-${q.id}`} value={v} onChange={(e) => setAns(q.id, e.target.value)} placeholder={q.type === "measurement" ? "mm" : ""} className="mt-1.5" />;
      if (q.type === "yes_no") return (<div className="mt-1.5 flex gap-2">{["Yes", "No"].map((o) => <button key={o} data-testid={`field-${q.id}-${o}`} onClick={() => setAns(q.id, o)} className={cn("rounded-lg border px-4 py-1.5 text-sm font-semibold", v === o ? "border-transparent bg-[#00A85A] text-white" : "border-[#E2E8F0] dark:border-[#223139] text-[#687173] dark:text-[#8B9698]")}>{o}</button>)}</div>);
      if (["single_choice", "dropdown"].includes(q.type)) return (<Select value={v} onValueChange={(val) => setAns(q.id, val)}><SelectTrigger className="mt-1.5" data-testid={`field-${q.id}`}><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{(q.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>);
      return <Input data-testid={`field-${q.id}`} value={v} onChange={(e) => setAns(q.id, e.target.value)} className="mt-1.5" />;
    })();
    return (
      <div key={q.id} className={q.type === "long_text" ? "sm:col-span-2" : ""}>
        <Label className="text-xs font-semibold text-[#101516] dark:text-white">{q.label}{q.required && <span className="text-red-500"> *</span>}{manualOnly && <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">MANUAL</span>}</Label>
        {inp}
      </div>
    );
  };

  const cur = steps[step];
  const isReview = cur?.kind === "review";
  const isImaging = cur?.kind === "imaging";
  const goNext = () => { if (cur?.kind === "section") save().catch(() => {}); setStep((s) => Math.min(s + 1, steps.length - 1)); };
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <div>
      <PageHeader breadcrumb={["Consultation", "Form", form.title]} title={form.title}
        subtitle={`${condition} · ${tier} · step-by-step manual entry`}
        actions={<Button variant="outline" onClick={() => navigate(`/consultation/${id}`)} className="rounded-lg"><Mic className="h-4 w-4 mr-1.5" />Use voice instead</Button>} />

      {/* Progress */}
      <SurfaceCard className="p-4 mb-5 sticky top-2 z-10">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold text-[#101516] dark:text-white">{cur?.title}</span>
          <span className="text-xs font-semibold text-[#00A85A]" data-testid="form-progress">Step {step + 1} of {steps.length} · {done}/{total}</span>
        </div>
        <div className="h-2 rounded-full bg-[#F4FBF7] dark:bg-[#0E1A21] overflow-hidden"><div className="h-full rounded-full bg-[#00A85A] transition-all" style={{ width: `${Math.round(((step + 1) / steps.length) * 100)}%` }} /></div>
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="form-step-dots">
          {steps.map((s, i) => (
            <button key={i} onClick={() => setStep(i)} data-testid={`form-step-${i}`}
              className={cn("h-1.5 flex-1 min-w-[16px] rounded-full transition-colors", i < step ? "bg-[#00A85A]" : i === step ? "bg-[#00A85A]" : "bg-[#E2E8F0] dark:bg-[#223139]")}
              title={s.title} />
          ))}
        </div>
      </SurfaceCard>

      {isImaging ? (
        <SurfaceCard className="p-6" data-testid="form-imaging-step">
          <ImagingUploadPanel consultationId={id} onChange={setImgState} />
          {imgState.count === 0 && <p className="mt-4 text-xs text-[#8B9698]">No investigations? You can continue — the AI will mark radiographic items as "requires assessment".</p>}
        </SurfaceCard>
      ) : !isReview ? (
        <SurfaceCard className="p-6" data-testid="form-step-content">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#E8F7F0] dark:bg-[#00A85A]/15 text-xs font-bold text-[#008F4D] dark:text-[#3FD98A]">{step + 1}</div>
            <h3 className="text-sm font-bold text-[#101516] dark:text-white">{cur.title}</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {cur.qs.map((q) => field(q))}
          </div>
        </SurfaceCard>
      ) : (
        <SurfaceCard className="p-6" data-testid="form-review">
          <div className="flex items-center gap-2 mb-4"><Check className="h-4 w-4 text-[#00A85A]" /><h3 className="text-sm font-bold text-[#101516] dark:text-white">Review captured details</h3></div>
          <div className="space-y-4">
            <div className="rounded-xl border border-[#E2E8F0] dark:border-[#223139] p-4" data-testid="review-imaging-summary">
              <div className="flex items-center justify-between"><span className="text-xs font-bold text-[#101516] dark:text-white">Investigations & Imaging</span><button onClick={() => setStep(steps.length - 2)} className="text-[11px] font-semibold text-[#00A85A] hover:underline" data-testid="review-edit-imaging">Add / review</button></div>
              <p className="mt-1 text-xs text-[#8B9698]">{imgState.count} file(s) attached{imgState.pending ? ` · ${imgState.pending} AI finding(s) awaiting your review` : imgState.count ? " · all findings reviewed" : ""}</p>
            </div>
            {sections.map((s) => {
              const filled = s.qs.filter((q) => answers[q.id] !== "" && answers[q.id] != null);
              return (
                <div key={s.title} className="rounded-xl border border-[#E2E8F0] dark:border-[#223139] p-4">
                  <div className="flex items-center justify-between mb-2"><span className="text-xs font-bold text-[#101516] dark:text-white">{s.title}</span><span className="text-[11px] text-[#8B9698]">{filled.length}/{s.qs.length}</span></div>
                  {filled.length ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                      {filled.map((q) => <div key={q.id} className="text-xs"><span className="text-[#8B9698]">{q.label}: </span><span className="font-semibold text-[#101516] dark:text-white">{String(answers[q.id])}</span></div>)}
                    </div>
                  ) : <span className="text-xs text-[#8B9698]">Nothing captured in this section.</span>}
                </div>
              );
            })}
          </div>
        </SurfaceCard>
      )}

      <div className="mt-6 flex flex-col sm:flex-row items-center gap-3">
        <Button variant="outline" onClick={goBack} disabled={step === 0} data-testid="form-back" className="h-11 rounded-full px-5"><ChevronLeft className="h-4 w-4 mr-1" />Back</Button>
        <div className="flex-1" />
        <Button variant="ghost" onClick={saveDraft} data-testid="form-save-draft" className="h-11 rounded-full">Save draft</Button>
        {!isReview ? (
          <Button onClick={goNext} data-testid="form-next" className="h-11 rounded-full bg-[#0B1512] px-6 text-white hover:bg-black dark:bg-white dark:text-[#0B1512]">Next<ChevronRight className="h-4 w-4 ml-1" /></Button>
        ) : (
          <Button onClick={analyze} disabled={busy || imgState.pending > 0 || imgState.analyzing > 0} data-testid="form-analyze-btn" className="h-11 rounded-full bg-[#00A85A] px-6 font-bold text-white hover:bg-[#008F4D]">
            {busy ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Analyzing…</> : imgState.analyzing > 0 ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Reading uploads…</> : imgState.pending > 0 ? <>Review {imgState.pending} imaging finding(s) first</> : <><Sparkles className="h-5 w-5 mr-2" />Analyze Case & Generate Treatment Plan</>}
          </Button>
        )}
      </div>
    </div>
  );
}
